import { useRef, memo, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import "./SmartDPMapList.scss";
import { VariableSizeList } from "react-window";
import memoizeOne from "memoize-one";
import SmartDpMapListItem from "app/pages/SmartDP/Map/SmartDPMapListItem";
import usePrevious from "app/utils/hooks/usePrevious";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import appGlobalsContext from "app/AppGlobalsContext";
import {
	QUOTATION_CODE_STATUS,
	SDP_MAP_PRODUCT_HEIGHT,
	SDP_MAP_PRODUCT_WITH_EMIRATES_PASS_HEIGHT,
	SDP_MAP_PRODUCT_SPOTLIGHT_HEIGHT,
	BRANDS,
} from "app/constants";
import { useLocation } from "react-router-dom";
import PaymentDescriptionContainer from "app/pages/.shared/DynamicBlocks/blocks/PaymentDescriptionBlock/PaymentDescriptionContainer";
import { useFlagship, EventCategory, HitType } from "@flagship.io/react-sdk";
import { useSelector } from "react-redux";
import { shouldShowPaymentBanner } from "app/reducers/partnerSelector";
import { ActionBar } from "app/features/product/productActionBar/ActionBar";
import { FLAGSHIP_EVENT_ON_SORT_SELECTION } from "app/features/product/productSort";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import LoaderBar from "app/pages/.shared/LoaderBar/LoaderBar";
import SdpPartialSuccessAlertMessage from "app/pages/SmartDP/Listing/SdpPartialSuccessAlertMessage/SdpPartialSuccessAlertMessage";
import AppGlobalsContext from "app/AppGlobalsContext";
import { getIsDubaiProduct } from "app/utils/productUtils";

const createItemData = memoizeOne(
	(
		accommodationsList,
		onAccommodationClick,
		itemSelected,
		onAccommodationHover,
		spotlightAccommodationId,
		specialOfferInfos,
		hasSdpSpecialOffer,
		quotationItems
	) => ({
		accommodationsList,
		onAccommodationClick,
		spotlightAccommodationId,
		itemSelected,
		onAccommodationHover,
		specialOfferInfos,
		hasSdpSpecialOffer,
		quotationItems,
	})
);

const itemKey = (index, data) => {
	const accommodation = data?.accommodationsList?.[index];
	return accommodation?.id ?? accommodation?.productId ?? accommodation?.name ?? index;
};

const GUTTER_SIZE = 24;
const PAYMENT_BANNER_HEIGHT = 88;

const SmartDPMapList = ({
	accommodationsList,
	itemSelected,
	isLoading,
	onAccommodationClick,
	scrollTo,
	fetchSDPProductsStatus,
	spotlightAccommodation = {},
	unavailableAccommodation = {},
	onAccommodationHover,
	specialOfferInfos,
	hasSdpSpecialOffer,
	title,
	quotationItems = [],
}) => {
	const { brand } = useContext(AppGlobalsContext);
	const { track } = useAnalytics();
	const { sendHits } = useFlagship();
	const isPaymentDescriptionBlockExist = useSelector(shouldShowPaymentBanner);

	const itemData = createItemData(
		accommodationsList,
		onAccommodationClick,
		itemSelected,
		onAccommodationHover,
		spotlightAccommodation?.id,
		specialOfferInfos,
		hasSdpSpecialOffer,
		quotationItems
	);

	// this value is used to calculate the height of the list.
	// dont forget to let some space below the list to avoid the last item to be hidden
	const HEADER_HEIGHT = isPaymentDescriptionBlockExist ? 291 + PAYMENT_BANNER_HEIGHT : 291;

	const { isRTL } = useContext(appGlobalsContext);
	const { pathname } = useLocation();
	const isStopover = pathname.includes("/stopover");

	const containerHeight =
		typeof window !== "undefined" ? window?.innerHeight - HEADER_HEIGHT : 1000;
	const rowCount = accommodationsList.length;

	const listRef = useRef();

	const previousChildren = usePrevious(scrollTo);

	const handleSubmit = () => {
		if (listRef?.current) {
			listRef.current.scrollToItem(0, "start");
		}
	};

	// VariableSizeList (react-window) caches item sizes internally and does not re-call getItemSize
	// for indices it has already measured, even when accommodationsList changes (e.g. after filtering
	// or sorting). An index would keep its old cached size even if a different accommodation now sits
	// there — causing the Emirates Pass or spotlight height to be applied to the wrong item.
	// resetAfterIndex(0) clears the entire size cache so getItemSize is called fresh for all items
	// on the next render.
	useEffect(() => {
		if (listRef?.current) {
			listRef.current.resetAfterIndex(0);
		}
	}, [accommodationsList]);

	useEffect(() => {
		if (scrollTo !== previousChildren && scrollTo.name) {
			const indexToModify = accommodationsList.findIndex(
				accommodation => accommodation.name === scrollTo.name
			);

			if (listRef?.current) {
				listRef.current.scrollToItem(indexToModify, "start");
			}
		}
	}, [scrollTo]);

	const getItemSize = index => {
		const accommodation = accommodationsList[index];
		const isDubaiProduct = getIsDubaiProduct(accommodation?.geography);
		const showEmiratesPassItem = brand === BRANDS.EK && isDubaiProduct;

		if (accommodation) {
			const spotlightHeight =
				accommodation.id === spotlightAccommodation?.id
					? SDP_MAP_PRODUCT_SPOTLIGHT_HEIGHT
					: 0;

			return (
				(showEmiratesPassItem
					? SDP_MAP_PRODUCT_WITH_EMIRATES_PASS_HEIGHT
					: SDP_MAP_PRODUCT_HEIGHT) +
				spotlightHeight +
				GUTTER_SIZE
			);
		}
		return 0;
	};

	const handleSortSubmit = sortById => {
		track("sort_product_list", { sortby: sortById });

		sendHits({
			type: HitType.EVENT,
			category: EventCategory.ACTION_TRACKING,
			action: FLAGSHIP_EVENT_ON_SORT_SELECTION[sortById],
		});
	};

	const handleFilterOpen = newPanel => {
		track(`listingPage_filter_by_${newPanel}_open`, {
			viewType: "map",
			filterPosition: "sticky",
		});
	};

	if (isLoading) {
		return (
			<section className="sdp-map-list" style={{ width: "710px", padding: "44px" }}>
				<div className="sdp-map-list__title sdp-map-list__title--loader">
					<LoaderBar width={230} />
				</div>

				<div style={{ marginBottom: "24px" }}>
					<ActionBar isSortAvailable={true} isLoading={true} />
				</div>

				{Array.from({ length: 2 }).map((_, index) => {
					return (
						<img
							key={index}
							style={{ width: "666px", marginBottom: "40px" }}
							src="/static/icons/map-card-skeleton-DESK.svg"
							alt="map-product-skeleton"
						/>
					);
				})}
			</section>
		);
	}

	return (
		<div className="sdp-map-list">
			{isPaymentDescriptionBlockExist && (
				<div className="sdp-map-list__payment-description">
					<PaymentDescriptionContainer />
				</div>
			)}

			<div className="sdp-map-list__header">
				<div className="sdp-map-list__title-box">
					<Typography
						variant={isStopover ? TYPOGRAPHY_VARIANTS.XL : TYPOGRAPHY_VARIANTS.REGULAR}
						isBold={isStopover ? true : false}
						className="sdp-map-list__title"
						component="h2"
					>
						{title}
					</Typography>
				</div>

				<div className="sdp-map-list__filter">
					<ActionBar
						isSortAvailable={true}
						isLoading={false}
						onFilterOpen={handleFilterOpen}
						onSortChange={handleSortSubmit}
						onSubmit={handleSubmit}
					/>
				</div>
			</div>

			{fetchSDPProductsStatus === QUOTATION_CODE_STATUS.PARTIAL_SUCCESS && (
				<SdpPartialSuccessAlertMessage accommodation={unavailableAccommodation} />
			)}

			<div>
				<VariableSizeList
					style={{ direction: isRTL ? "rtl" : "ltr" }}
					className="sdp-map-list-container"
					height={containerHeight}
					overscanCount={1}
					itemCount={rowCount}
					ref={listRef}
					width={710}
					itemSize={getItemSize}
					itemData={itemData}
					itemKey={itemKey}
				>
					{SmartDpMapListItem}
				</VariableSizeList>
			</div>
		</div>
	);
};

SmartDPMapList.propTypes = {
	specialOfferInfos: PropTypes.object,
	hasSdpSpecialOffer: PropTypes.bool,
	fetchSDPProductsStatus: PropTypes.string,
	spotlightAccommodation: PropTypes.object,
	unavailableAccommodation: PropTypes.object,
	isLoading: PropTypes.bool,
	accommodationsList: PropTypes.array,
	itemSelected: PropTypes.string,
	scrollTo: PropTypes.shape({ name: PropTypes.string, seq: PropTypes.number }),
	onAccommodationClick: PropTypes.func,
	onAccommodationHover: PropTypes.func,
	title: PropTypes.object,
	quotationItems: PropTypes.array,
};

export default memo(SmartDPMapList);
